import { useCallback, useRef, useState } from "react";

/**
 * Width of an element in CSS pixels, kept current as it resizes.
 *
 * The demand charts are drawn at their real pixel size rather than scaled
 * through a viewBox: scaling would stretch the text and the 2px marks along
 * with the plot, and a bar capped at 24px would stop being 24px.
 *
 * The ref is a callback, so the observer follows the element: a chart that
 * is unmounted behind a Chart/Table toggle and mounted again gets measured
 * again, instead of watching a detached node and reporting zero for good.
 * React hands the callback null when the element goes, which is where the
 * observer is dropped — not in an effect, whose development-mode double run
 * would disconnect it while the element is still on the page.
 */
export function useElementWidth<T extends HTMLElement>(): [(el: T | null) => void, number] {
  const [width, setWidth] = useState(0);
  const observer = useRef<ResizeObserver | null>(null);

  const ref = useCallback((el: T | null) => {
    observer.current?.disconnect();
    observer.current = null;
    if (!el) return;
    const measure = () => setWidth(Math.floor(el.clientWidth));
    measure();
    observer.current = new ResizeObserver(measure);
    observer.current.observe(el);
  }, []);

  return [ref, width];
}
